import { createFileRoute } from "@tanstack/react-router";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, boolean, array, string, number } from "yup";
import { Switch, Button } from "@heroui/react";
import SelectInput from "@/components/inputs/SelectInput";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import { useState, useEffect } from "react";
import SelectMultipleInput from "@/components/inputs/SelectMultipleInput";
import { useTranslation } from "react-i18next";

type FormData = {
    send_text_notifications: boolean;
    send_email_notifications: boolean;
    timezone: string;
    days_to_notify?: number[];
    notification_delivery_time_1: string;
};

type UserData = FormData;

const schema = object({
    send_text_notifications: boolean().required().label("Text Notifications"),
    send_email_notifications: boolean().required().label("Email Notifications"),
    timezone: string().required().label("Timezone"),
    days_to_notify: array()
        .of(number().required())
        .required()
        .label("Days to receive notifications"),
    notification_delivery_time_1: string().required().label("Delivery Time"),
});

export const Route = createFileRoute(
    "/dashboard/account-settings/notifications",
)({
    component: NotificationsComponent,
});

function NotificationsComponent() {
    const { t } = useTranslation("messages");
    const { user } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();
    const [isLoading, setIsLoading] = useState(false);

    const form = useForm<FormData>({
        resolver: yupResolver<FormData>(schema),
        defaultValues: {
            send_text_notifications: true,
            send_email_notifications: true,
            timezone: "US/Central",
            days_to_notify: [1],
            notification_delivery_time_1: "07:00",
        },
    });

    const { data: userData } = useGetRequest<UserData>({
        url: `/users/${user?.id}/`,
        enabled: !!user?.id,
    });

    useEffect(() => {
        if (userData) {
            const deliveryTime =
                userData.notification_delivery_time_1 || "07:00";
            const [hours, minutes] = deliveryTime.split(":");
            const formattedTime = `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;

            form.reset({
                send_text_notifications: userData.send_text_notifications,
                send_email_notifications: userData.send_email_notifications,
                timezone: userData.timezone,
                days_to_notify:
                    (userData.days_to_notify?.map((day) =>
                        day.toString(),
                    ) as unknown as number[]) || [],
                notification_delivery_time_1: formattedTime,
            });
        }
    }, [userData, form]);

    const updateSettings = useMutateRequest({
        url: `/users/${user?.id}/`,
        method: "put",
        successMessage: t("success.notification_settings_updated"),
        errorMessage: t("error.failed_to_update_notification_settings"),
    });

    const onSubmit = (data: FormData) => {
        setIsLoading(true);

        const selectedDays = Array.from(
            new Set(
                (data.days_to_notify || []).map((day) =>
                    typeof day === "string" ? Number(day) : day,
                ),
            ),
        ).filter((day) => !isNaN(day)) as number[];

        const completeUserData = {
            username: user?.username,
            email: user?.email,
            first_name: user?.first_name,
            last_name: user?.last_name,
            is_active: user?.is_active,
            is_staff: user?.is_staff,
            account_type: user?.account_type,
            agreed_to_terms: user?.agreed_to_terms,
            address_1: user?.address_1,
            address_2: user?.address_2,
            city: user?.city,
            state: user?.state,
            zip: user?.zip,
            phone_number: user?.phone_number,
            crm_id: user?.crm_id,
            leaf_id: user?.leaf_id,
            organization: user?.organization,
            mobile_id: user?.mobile_id,
            send_text_notifications: data.send_text_notifications,
            send_email_notifications: data.send_email_notifications,
            send_push_notifications: user?.send_push_notifications,
            timezone: data.timezone,
            days_to_notify: selectedDays,
            notification_delivery_time_1: data.notification_delivery_time_1,
        };
        updateSettings.mutate(
            { data: completeUserData },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    form.reset({
                        ...data,
                        days_to_notify: selectedDays.map((day) =>
                            day.toString(),
                        ) as unknown as number[],
                    });
                },
                onError: () => {
                    setIsLoading(false);
                },
            },
        );
    };

    const timezoneOptions = [
        { id: "US/Pacific", description: t("notifications.pacific_time", { ns: "accountSettings" }) },
        { id: "US/Mountain", description: t("notifications.mountain_time", { ns: "accountSettings" }) },
        { id: "US/Central", description: t("notifications.central_time", { ns: "accountSettings" }) },
        { id: "US/Eastern", description: t("notifications.eastern_time", { ns: "accountSettings" }) },
    ];

    const daysOptions = [
        { id: 0, description: t("notifications.monday", { ns: "accountSettings" }) },
        { id: 1, description: t("notifications.tuesday", { ns: "accountSettings" }) },
        { id: 2, description: t("notifications.wednesday", { ns: "accountSettings" }) },
        { id: 3, description: t("notifications.thursday", { ns: "accountSettings" }) },
        { id: 4, description: t("notifications.friday", { ns: "accountSettings" }) },
        { id: 5, description: t("notifications.saturday", { ns: "accountSettings" }) },
        { id: 6, description: t("notifications.sunday", { ns: "accountSettings" }) },
    ];

    const generateTimeOptions = () => {
        const timeOptions: Array<{ id: string; description: string }> = [];

        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const hour24 = hour.toString().padStart(2, "0");
                const minute24 = minute.toString().padStart(2, "0");
                const id = `${hour24}:${minute24}`;

                let hour12 = hour % 12;
                if (hour12 === 0) hour12 = 12;
                const period =
                    hour < 12 ? t("notifications.am", { ns: "accountSettings" }) : t("notifications.pm", { ns: "accountSettings" });
                const description = `${hour12}:${minute24 === "00" ? "00" : minute24} ${period}`;

                timeOptions.push({ id, description });
            }
        }

        return timeOptions;
    };

    const timeOptions = generateTimeOptions();

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col gap-6 max-w-2xl p-2"
            >
                <h1 className="text-2xl font-bold">
                    {t("notifications.title", { ns: "accountSettings" })}
                </h1>

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <label htmlFor="text-notifications" className="text-sm">
                            {t("notifications.text_notifications", { ns: "accountSettings" })}
                        </label>
                        <Switch
                            isSelected={form.watch("send_text_notifications")}
                            onValueChange={(value) =>
                                form.setValue("send_text_notifications", value)
                            }
                            size="sm"
                        />
                    </div>

                    <div className="flex items-center justify-between">
                        <label
                            htmlFor="email-notifications"
                            className="text-sm"
                        >
                            {t("notifications.email_notifications", { ns: "accountSettings" })}
                        </label>
                        <Switch
                            isSelected={form.watch("send_email_notifications")}
                            onValueChange={(value) =>
                                form.setValue("send_email_notifications", value)
                            }
                            size="sm"
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-4">
                    <h2 className="text-lg font-semibold">
                        {t("notifications.delivery_settings", { ns: "accountSettings" })}
                    </h2>

                    <SelectInput
                        name="notification_delivery_time_1"
                        options={timeOptions}
                        label={t("notifications.delivery_time", { ns: "accountSettings" })}
                        isRequired={true}
                        size="sm"
                    />

                    <SelectInput
                        name="timezone"
                        options={timezoneOptions}
                        label={t("notifications.timezone", { ns: "accountSettings" })}
                    />

                    <SelectMultipleInput
                        name="days_to_notify"
                        options={daysOptions}
                        label={t("notifications.days_to_notify", { ns: "accountSettings" })}
                    />
                </div>

                <Button
                    type="submit"
                    color="primary"
                    isLoading={isLoading}
                    className="mt-4"
                >
                    {t("notifications.save_settings", { ns: "accountSettings" })}
                </Button>
            </form>
        </FormProvider>
    );
}
